import debounce from 'lodash/debounce';
import { AppContext } from 'src/components/app-context';
import { useState, useContext, useCallback, useEffect } from 'react';
import type { SupportedSection, SectionGroup } from '../filter-section';
import RouteContext from '../../../../routes/context';

const SEARCH_THROTTLE = 500;
const debouncedSearch = debounce(
  (query: string, dispatch: (action: any) => void) =>
    dispatch({
      type: 'GLOBAL_AGGREGATION_SEARCH',
      globalPropertySearch: query.length > 1 ? query : '', // one letter searches return empty
    }),
  SEARCH_THROTTLE
);

export const useGlobalPropertySearch = () => {
  const { dispatch, getState } = useContext(RouteContext);
  const { market } = useContext(AppContext);
  const [filterSearchQuery, setFilterSearchQuery] = useState<string>('');
  useEffect(() => {
    const state = getState();
    setFilterSearchQuery(state.globalPropertySearch as string);
    // Set an empty query when the component dismount
    return () =>
      dispatch({
        type: 'GLOBAL_AGGREGATION_SEARCH',
        globalPropertySearch: '',
      });
  }, [dispatch, getState]);
  const locale = market.locale.replace('_', '-');
  const isSearching = !!filterSearchQuery;
  const onSecondarySearchFieldChange = (query: string) => {
    setFilterSearchQuery(query);
    debouncedSearch(query, dispatch);
  };

  const filterByPropertySearch = useCallback(
    (section: SupportedSection) => {
      if (!isSearching) {
        return true;
      }
      if (section.__typename === 'ListSection' && section?.items?.length) {
        return true;
      }
      const matchTitle = section.title
        .toLocaleLowerCase(locale)
        .includes(filterSearchQuery?.toLocaleLowerCase(locale));

      return matchTitle;
    },
    [filterSearchQuery, locale, isSearching]
  );

  const hasSectionMatchingFilter = useCallback(
    (group: SectionGroup) => group.sections.some(filterByPropertySearch),
    [filterByPropertySearch]
  );

  return {
    isSearching,
    filterSearchQuery,
    filterByPropertySearch,
    hasSectionMatchingFilter,
    onSecondarySearchFieldChange,
  };
};

export type GlobalPropertySearch = ReturnType<typeof useGlobalPropertySearch>;
